/**
 * **What a family calls each kind of durable memory** — the five headings and the five nouns,
 * in one table, because two surfaces now speak them (issue #302).
 *
 * `show_what_you_know` groups a family's record under these headings in the conversation
 * (`shapeInventory.ts`), and the control panel puts the same five on a screen. A family who
 * reads *"What you're working toward"* in Claude and *"Your goals"* in the panel has been
 * shown two names for one box, and would reasonably conclude they are two different boxes.
 *
 * **This is the metric-names argument, applied to words a family reads** rather than to a
 * counter an alarm reads: a drift between the two emitters breaks no build, fails no test that
 * anybody thought to write, and is invisible until somebody uses both surfaces. It is also the
 * argument `contextNouns.ts` already made once and won — *"one table, because there were two
 * and they drifted"* — one surface later.
 *
 * **`ContextType` itself deliberately stays in `@pilotfish/db`.** Moving it would follow
 * {@link WriteChannel}'s precedent and would be the tidier shape, but it is 85 references
 * across 22 files and none of them is what this issue is about. What has to be shared is the
 * *vocabulary*, not the union — so the union stays where the table defines it, and
 * `shapeInventory` keeps annotating its lookup as `Record<ContextType, string>`, which is what
 * makes a divergence between these five literals and those five a compile error rather than a
 * discovery.
 *
 * There is no runtime dependency here and there must not be: a browser imports this.
 */

/**
 * The five kinds of durable memory, in the order a family is shown them.
 *
 * The order is `shapeInventory`'s `SECTION_ORDER` and is deliberate rather than alphabetical:
 * what a family is working toward comes before what constrains it, and the catch-all comes
 * last.
 */
export const CONTEXT_KIND_ORDER = ["GOAL", "CONSTRAINT", "PREFERENCE", "DECISION", "FACT"] as const;

export type ContextKind = (typeof CONTEXT_KIND_ORDER)[number];

/**
 * The heading a family reads above facts of this kind.
 *
 * A sentence about them rather than a category name: `GOAL` is a key, *"What you're working
 * toward"* is an answer. `remember`'s `contextType` description quotes the `FACT` heading
 * word for word (issue #236) so a host filing a fact and a family reading it back are told the
 * same thing about the same box, and `tool-descriptions-speak-family-language.test.ts` holds
 * that against this table's real value rather than a second copy of the string.
 */
export const CONTEXT_KIND_HEADING: Readonly<Record<ContextKind, string>> = {
  GOAL: "What you're working toward",
  CONSTRAINT: "What you've asked me to work around",
  PREFERENCE: "How you like things done",
  DECISION: "What you've decided",
  FACT: "Things about your family I keep in mind",
};

/**
 * What to call **one** fact of this kind when speaking to a family — "goal", not `GOAL`.
 *
 * Moved here from `services/mcp/src/tools/contextNouns.ts`, whose header records what these
 * cost when there were two copies: `remember` and `forget_this` each kept their own, #92 gave
 * a fact a subject, one copy was corrected and the other was not, and deleting a member's
 * personal goal announced *"the family goal you asked me to remember"* — on the one tool with
 * no undo.
 *
 * **The noun names the kind and never whose it is.** Whose belongs to the subject, and only a
 * caller holding the row can say it.
 */
export const CONTEXT_KIND_NOUN: Readonly<Record<ContextKind, string>> = {
  GOAL: "goal",
  CONSTRAINT: "constraint",
  PREFERENCE: "preference",
  DECISION: "decision",
  FACT: "note",
};

/**
 * Whether a string off the wire is one of the five.
 *
 * The panel needs this and the server does not: a `/app` read discloses `kind` as a plain
 * string (`disclosure.ts` projects `item.contextType`), and a browser cannot assume the field
 * it was handed is one this build knows — a sixth kind added server-side reaches an older
 * bundle as a heading it has no words for. Narrowing it here means the caller has to decide
 * what to do about that, rather than indexing a record with `undefined` and rendering it.
 */
export function isContextKind(value: string): value is ContextKind {
  return (CONTEXT_KIND_ORDER as readonly string[]).includes(value);
}
